import Fraction from "fraction.js";

// Normalize any value into a safe numeric input for Fraction
const normalizeNumber = (value, fallback = 0) => {
  if (value instanceof Fraction) return value.valueOf();
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) return numeric;
    // Allow strings like "1/2" to be parsed by fraction.js, otherwise fallback
    try {
      return new Fraction(trimmed).valueOf();
    } catch (e) {
      return fallback;
    }
  }
  return fallback;
};

const buildFraction = (n, d = 1) => {
  try {
    return new Fraction(n, d);
  } catch (e) {
    // Gracefully degrade to zero instead of throwing
    return new Fraction(0);
  }
};

// Creates a simplified fraction from numerator/denominator (defaults to integer)
export const R = (n, d = 1) => {
  const num = normalizeNumber(n, 0);
  const den = normalizeNumber(d, 1) || 1;
  return buildFraction(num, den);
};

// Normalizes any supported input into a Fraction instance
export const asFraction = (value) => {
  if (value instanceof Fraction) return value;
  if (value && typeof value === "object" && "numerator" in value && "denominator" in value) {
    return fromJSON(value);
  }
  if (value && typeof value === "object" && "n" in value && "d" in value) {
    return R(value.n, value.d);
  }
  if (typeof value === "string" || typeof value === "number") {
    return R(value);
  }
  return R(0);
};

// Safely build a fraction from persisted shape { numerator, denominator }
export const fromJSON = (value) => {
  if (!value || typeof value !== "object") return R(0);
  const { numerator, denominator } = value;
  return R(numerator ?? 0, denominator ?? 1);
};

// Persistable plain object to keep Vue reactivity (avoid class instances in state)
export const toJSON = (fraction) => {
  const f = asFraction(fraction);
  return { numerator: f.n, denominator: f.d };
};

// Numeric helper for charts/Excel
export const toNumber = (fraction) => asFraction(fraction).valueOf();

// Percent helper (keeps precision while returning number)
export const toPercentNumber = (fraction) => toNumber(fraction) * 100;

// String helper if UI ever needs "1/2"
export const toFractionString = (fraction) => asFraction(fraction).toFraction(true);
